import {
  addMinutes,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  format,
  formatRelative as formatRelativeDateFns,
  isValid,
  parseISO,
} from 'date-fns';

function getUserTimezoneOffset() {
  return new Date().getTimezoneOffset();
}

export function newDateDefaultUTC(date: string): Date {
  return hasTimezone(date) ? new Date(date) : new Date(date + 'Z');
}

export function isFutureDate(date: string) {
  return newDateDefaultUTC(date) > new Date();
}

function hasTimezone(input: string): boolean {
  return /([+-]\d{2}:\d{2}|Z)$/.test(input);
}

export function dateOnly(datetimeStr: string): string {
  return datetimeStr.split('T')[0] ?? datetimeStr;
}

export function formatDate(
  timestamp: string,
  type: 'short' | 'long' | 'slashed' | 'default' | 'default-short' | 'slashed-short' = 'short'
) {
  const utc = newDateDefaultUTC(timestamp);
  const localDate = new Date(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate());

  if (isValid(localDate)) {
    timestamp = localDate.toISOString();
  }

  if (type === 'slashed') {
    const timezoneAdjustedTimestamp = convertToUserTime(new Date(timestamp)).toISOString();
    return timezoneAdjustedTimestamp.slice(0, 10).replace(/-/g, '/');
  }

  if (type === 'slashed-short') {
    const date = new Date(timestamp);
    return format(date, 'M/d/yy'); // Formats as "M/D/YY" for slashed-short
  }

  if (type === 'default') {
    const date = timestamp.includes('T')
      ? new Date(timestamp)
      : new Date(timestamp.replace(/-/g, '/'));
    return format(date, 'MM/dd/yyyy');
  }

  if (type === 'default-short') {
    return format(new Date(timestamp), 'MM/dd/yy');
  }

  return new Intl.DateTimeFormat('en-US', {
    month: type,
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(timestamp));
}

function convertToUserTime(datetime: number | Date | string) {
  const fixedDate =
    typeof datetime === 'number' || typeof datetime === 'string' ? new Date(datetime) : datetime;
  const utcDate = parseISO(fixedDate.toISOString());
  const userTimezoneOffset = getUserTimezoneOffset();
  return addMinutes(utcDate, -userTimezoneOffset);
}

// timezone-aware 'formatRelative' date-fns wrapper
export const formatRelative: typeof formatRelativeDateFns = (date, baseDate, options) => {
  const utcOffsetDate = convertToUserTime(date);
  return formatRelativeDateFns(utcOffsetDate, baseDate, options);
};

export const getRelativeTime = (date: Date | string) => {
  const dateStr = typeof date === 'string' ? date : date.toISOString();
  const shouldConvert = !hasTimezone(dateStr);
  const utcOffsetDate = shouldConvert ? convertToUserTime(date) : new Date(date);
  const now = new Date();

  const minutesDifference = differenceInMinutes(utcOffsetDate, now);
  const hoursDifference = differenceInHours(utcOffsetDate, now);
  const daysDifference = differenceInDays(utcOffsetDate, now);

  const absMinutes = Math.abs(minutesDifference);
  const absHours = Math.abs(hoursDifference);
  const absDays = Math.abs(daysDifference);

  let text = '';
  let color = '';

  if (absMinutes < 60) {
    text = `${absMinutes} minute${absMinutes !== 1 ? 's' : ''}`;
  } else if (absHours < 24) {
    text = `${absHours} hour${absHours !== 1 ? 's' : ''}`;
  } else {
    text = `${absDays} day${absDays !== 1 ? 's' : ''}`;
  }

  if (minutesDifference < 0) {
    text += ' ago';
  } else if (minutesDifference > 0) {
    text = 'In ' + text;
  } else {
    text = 'Just now';
  }

  if (absDays >= 7) {
    color = 'text-red-500';
  } else if (absDays >= 3) {
    color = 'text-yellow-400';
  } else {
    color = 'text-gray-500';
  }

  return {
    text,
    color,
  };
};

/**
 * Formats a date/timestamp in Eastern Time (EST/EDT)
 * Always includes "ET" suffix to clarify timezone.
 */
export function formatDateTimeInET(
  date: Date | string,
  options?: { includeDate?: boolean }
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const includeDate = options?.includeDate ?? true;

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    ...(includeDate && { month: 'short', day: 'numeric', year: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return `${formatter.format(dateObj)} ET`;
}
